'use client'

import { useEffect, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import clsx from 'clsx'
import { usePathname, useRouter } from 'next/navigation'
import { useSession } from 'next-auth/react'

import { reportSentryError } from 'lib/sentry'
import { SyneriseFormEvent, syneriseSendFormData } from 'lib/synerise'
import { getQueryClient, QueryKey } from 'shared/api'
import { AddressToDisplayInterface, FormMode } from 'shared/types/forms'
import Button from 'shared/ui/Button'
import {
  ControlledCheckbox
} from 'shared/ui/form/ControlledElements/ControlledCheckbox'
import {
  ControlledHiddenField
} from 'shared/ui/form/ControlledElements/ControlledHiddenField'
import {
  ControlledTextField
} from 'shared/ui/form/ControlledElements/ControlledTextField'
import toast from 'shared/ui/utils/toast'
import { getApiErrors } from 'shared/utils/errors'
import { addTestId } from 'shared/utils/tests'
import {
  FloorNumberRegex,
  PhoneNumberRegex,
  PostalCodeRegex
} from 'shared/utils/validators'

import { useDeliveryAddressPort } from '../../../../ports'
import { extractTokenFromClientSession } from '../../../../session'
import { addDeliveryAddress, editCustomerDeliveryAddress } from '../../api'
import { AddressServiceSingleton } from '../../mappers'
import { autoCompletePostalCode } from '../../postalCodes'
import {
  colorsData,
  CustomerAddress,
  CustomerAddressType,
  CustomerDeliveryAddressFormValues,
  iconsData,
  ResponseStatus
} from '../../types'
import {
  validateCustomerAddress,
  ValidateCustomerAddressResult
} from '../../utils'
import GoogleAddressInput from '../AddressAutocompleteInput'
import ConfirmAddressModal from '../ConfirmAddressModal'
import { ControlledOptionRow } from '../ControlledOptionRow'

import styles from './FormAddDeliveryAddress.module.scss'

interface FormAddDeliveryAddressProps {
  mode: FormMode
  handleClose: () => void
  defaultValues: CustomerDeliveryAddressFormValues
  isFirstAddressToAdd: boolean
}

const FormAddDeliveryAddress = ({
  defaultValues,
  mode,
  handleClose,
  isFirstAddressToAdd
}: FormAddDeliveryAddressProps) => {
  const { checkDeliveryCoverage, onDeliveryAddressSaved } =
    useDeliveryAddressPort()
  const [submitState, setSubmitState] = useState<ResponseStatus>(
    ResponseStatus.initial
  )
  const router = useRouter()
  const pathname = usePathname()
  const session = useSession()
  const token = extractTokenFromClientSession(session)
  const methods = useForm<CustomerDeliveryAddressFormValues>({
    mode: 'onChange',
    defaultValues
  })

  const postalCodeValue = methods.watch('postalCode')
  const [confirmAddressModalOpen, setConfirmAddressModalOpen] = useState(false)
  const [addressValidationState, setAddressValidationState] =
    useState<ValidateCustomerAddressResult | null>(null)

  const isDefaultAddressDisabled =
    (defaultValues.isDefault && mode === FormMode.edit) || isFirstAddressToAdd

  const handleUpdateForm = (payload: AddressToDisplayInterface) => {
    const values = methods.getValues()
    const preparePayload: CustomerDeliveryAddressFormValues = {
      ...values,
      ...payload
    }

    methods.reset(preparePayload)
  }

  const prepareFullAddress = () => {
    const defStreet = defaultValues?.street ? `${defaultValues?.street} ` : ''
    const defHouseNumber = defaultValues?.houseNumber
      ? `${defaultValues?.houseNumber},  `
      : ''
    const defCity = defaultValues?.city ? `${defaultValues?.city},  ` : ''
    const defCountry = defStreet || defHouseNumber || defCity ? `Polska` : ''

    return `${defStreet}${defHouseNumber}${defCity}${defCountry}`
  }

  useEffect(() => {
    methods.setValue('postalCode', autoCompletePostalCode(postalCodeValue))
  }, [postalCodeValue])

  const _handleClose = async () => {
    /**
     * Refresh props from getServerSideProps
     * More: https://www.joshwcomeau.com/nextjs/refreshing-server-side-props/
     */
    void router.replace(pathname)
    handleClose()
  }

  const handleConfirmAddressModalClose = () => {
    setConfirmAddressModalOpen(false)
    setAddressValidationState(null)
  }

  const onSubmit: SubmitHandler<
    CustomerDeliveryAddressFormValues
  > = async values => {
    const { postalCode, city, street, houseNumber } = values

    setSubmitState(ResponseStatus.pending)

    try {
      const addressValidation = await validateCustomerAddress(
        {
          postalCode,
          city,
          street,
          houseNumber: houseNumber.toLowerCase()
        },
        checkDeliveryCoverage
      )

      const payload = AddressServiceSingleton.mapDeliveryFormValuesToPayload(
        { ...values, ...addressValidation?.coordinates },
        CustomerAddressType.delivery
      )

      if (
        addressValidation?.isAnyUnconfirmed ||
        addressValidation?.hasDifferences ||
        !addressValidation?.hasAnySlot
      ) {
        setConfirmAddressModalOpen(true)
        setAddressValidationState(addressValidation)
        setSubmitState(ResponseStatus.rejected)
      } else {
        if (mode === FormMode.create) {
          const response = await addDeliveryAddress(payload, token)

          if ([200, 201].includes(response.status)) {
            const responseId = response.data.data?.id

            if (responseId) {
              onDeliveryAddressSaved(responseId, FormMode.create)
            }

            // Optimistically update the cache
            getQueryClient().setQueryData(
              [QueryKey.deliveryAddresses, token],
              (oldData: CustomerAddress[] | undefined) => {
                const newAddress = response.data.data

                let prevData = oldData || []

                if (newAddress.isDefault) {
                  prevData = prevData.map(item => ({
                    ...item,
                    isDefault: false
                  }))
                }

                return [...prevData, newAddress]
              }
            )

            await _handleClose()
            toast.success('Dane zostały zapisane')

            syneriseSendFormData({
              formData: {
                deliveryCity: payload.city,
                deliveryPostalCode: payload.postalCode
              },
              event: SyneriseFormEvent.ADDRESS_CREATE,
              fieldMapping: {
                city: 'city'
              }
            })

            setSubmitState(ResponseStatus.fulfilled)
          } else {
            setSubmitState(ResponseStatus.rejected)
          }
        } else if (mode === FormMode.edit) {
          const response = await editCustomerDeliveryAddress(values.id, payload, token)

          if ([200, 201].includes(response.status)) {
            const responseId = response.data.data?.id

            if (responseId) {
              onDeliveryAddressSaved(responseId, FormMode.edit)
            }

            getQueryClient().setQueryData(
              [QueryKey.deliveryAddresses, token],
              (oldData: CustomerAddress[] | undefined) => {
                return (
                  oldData?.map((address: CustomerAddress) => {
                    const newItem = response.data.data

                    const isEditedItem = address.id === values.id

                    if (isEditedItem) {
                      return newItem
                    }

                    return newItem.isDefault
                      ? { ...address, isDefault: false }
                      : address
                  }) || []
                )
              }
            )

            await _handleClose()

            toast.success(`Adres "${values.name}" został zaktualizowany.`)

            syneriseSendFormData({
              formData: {
                deliveryCity: payload.city,
                deliveryPostalCode: payload.postalCode
              },
              event: SyneriseFormEvent.ADDRESS_EDIT,
              fieldMapping: {
                city: 'city'
              }
            })

            setSubmitState(ResponseStatus.fulfilled)
          } else {
            setSubmitState(ResponseStatus.rejected)
          }
        }
      }
    } catch (error) {
      reportSentryError(
        error,
        'An error has occurred when creating/editing invoice address:'
      )
      toast.error(getApiErrors(error))
      setSubmitState(ResponseStatus.rejected)
    }
  }

  return (
    <>
      <ConfirmAddressModal
        open={confirmAddressModalOpen}
        onClose={handleConfirmAddressModalClose}
        onUpdate={methods.setValue}
        addressValidation={addressValidationState}
        flatNumber={methods.getValues('flatNumber')}
        staircaseNumber={methods.getValues('staircaseNumber')}
      />
      <FormProvider {...methods}>
        <form noValidate onSubmit={methods.handleSubmit(onSubmit)}>
          <ControlledHiddenField name='id' />
          <div className={styles.row}>
            {isDefaultAddressDisabled ? (
              <div className={styles.addressDefaultNotice}>
                Obecnie wybrany adres główny
              </div>
            ) : (
              <ControlledCheckbox
                id='delivery_address_default'
                disabled={isDefaultAddressDisabled}
                name='isDefault'
                label='Ustaw jako główny adres dostawy'
                defaultValue={defaultValues.isDefault}
              />
            )}
          </div>
          <div className={styles.row}>
            <ControlledTextField
              id='delivery_address_name'
              label='Nazwa twojego adresu'
              placeholder='np. "domowy" lub "dla rodziców"'
              name='name'
              required
              rules={{
                required: 'Nazwa adresu jest wymagana.',
                minLength: {
                  value: 3,
                  message: 'Min. 3 znaki.'
                },
                maxLength: {
                  value: 256,
                  message: 'Zbyt długa nazwa.'
                }
              }}
              noMargin
            />
            <ControlledOptionRow
              id='delivery_address_icon'
              type='icons'
              label='Ikona:'
              defaultValue={iconsData[0].value}
              data={iconsData}
              name='iconType'
            />
            <ControlledOptionRow
              id='delivery_address_color'
              type='colors'
              label='Kolor:'
              defaultValue={colorsData[0].value}
              data={colorsData}
              name='iconColor'
              {...addTestId('delivery-address-color')}
            />
          </div>
          <div className={styles.subtitle}>Wyszukaj adres</div>
          <div className={clsx(styles.row, styles.noMargin)}>
            <GoogleAddressInput
              handleUpdateForm={handleUpdateForm}
              prepareFullAddress={prepareFullAddress}
            />
          </div>
          <hr className={styles.hr} />
          <div className={styles.subtitle}>Uzupełnij dane</div>
          <div className={styles.row}>
            <ControlledTextField
              id='delivery_address_first_name'
              label='Imię'
              placeholder='Imię'
              name='firstName'
              required
              rules={{
                required: 'Imię jest wymagane.',
                minLength: {
                  value: 3,
                  message: 'Min. 3 znaki.'
                },
                maxLength: {
                  value: 64,
                  message: 'Zbyt długie imię.'
                }
              }}
              noMargin
            />
            <ControlledTextField
              id='delivery_address_last_name'
              label='Nazwisko'
              placeholder='Nazwisko'
              required
              name='lastName'
              rules={{
                required: 'Nazwisko jest wymagane.',
                minLength: {
                  value: 3,
                  message: 'Min. 3 znaki.'
                },
                maxLength: {
                  value: 64,
                  message: 'Zbyt długie nazwisko.'
                }
              }}
              noMargin
            />
          </div>
          <div className={styles.row}>
            <ControlledTextField
              id='delivery_address_phone'
              label='Numer telefonu'
              name='phoneNumber'
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*'
              }}
              startIcon={<div>+48</div>}
              required
              rules={{
                required: 'Numeru telefonu jest wymagany.',
                minLength: {
                  value: 9,
                  message: 'Min. 9 znaków'
                },
                maxLength: {
                  value: 12,
                  message: 'Maks. 12 znaków'
                },
                pattern: {
                  value: PhoneNumberRegex,
                  message: 'Numer wygląda na nieprawidłowy.'
                }
              }}
              noMargin
            />
          </div>
          <div className={styles.row}>
            <ControlledTextField
              id='delivery_address_postal'
              label='Kod pocztowy'
              placeholder='xx-xxx'
              name='postalCode'
              required
              rules={{
                required: 'Kod pocztowy jest wymagany.',
                maxLength: {
                  value: 6,
                  message: 'Kod pocztowy jest zbyt długi'
                },
                pattern: {
                  value: PostalCodeRegex,
                  message: 'Nieprawidłowy format.'
                }
              }}
              type='tel'
              noMargin
            />

            <ControlledTextField
              id='delivery_address_city'
              label='Miejscowość'
              placeholder='Nazwa miejscowości'
              name='city'
              required
              rules={{
                required: 'Nazwa jest wymagana.',
                minLength: {
                  value: 3,
                  message: 'Min. 3 znaki.'
                }
              }}
              noMargin
            />
          </div>
          <div className={styles.row}>
            <ControlledTextField
              id='delivery_address_street'
              label='Ulica'
              placeholder='Nazwa ulicy'
              name='street'
              required
              rules={{
                required: 'Nazwa jest wymagana.',
                minLength: {
                  value: 3,
                  message: 'Min. 3 znaki.'
                }
              }}
              noMargin
            />
            <ControlledTextField
              id='delivery_address_house_number'
              label='Numer budynku'
              placeholder='Numer budynku'
              name='houseNumber'
              required
              rules={{
                required: 'Numer jest wymagany.',
                maxLength: {
                  value: 12,
                  message: 'Maks. 12 znaków.'
                }
              }}
              noMargin
            />
            <ControlledTextField
              id='delivery_address_staircase'
              label='Klatka schodowa'
              placeholder='Klatka schodowa'
              name='staircaseNumber'
              rules={{
                maxLength: {
                  value: 3,
                  message: 'Maks. 3 znaki.'
                }
              }}
              noMargin
            />
          </div>
          <div className={styles.row}>
            <ControlledTextField
              id='delivery_address_flat_number'
              label='Numer mieszkania'
              placeholder='Numer mieszkania'
              name='flatNumber'
              rules={{
                maxLength: {
                  value: 4,
                  message: 'Maks. 4 znaki.'
                }
              }}
              noMargin
            />
            <ControlledTextField
              id='delivery_address_floor'
              label='Piętro'
              placeholder='Piętro'
              name='floor'
              rules={{
                maxLength: {
                  value: 3,
                  message: 'Maks. 3 znaki.'
                },
                pattern: {
                  value: FloorNumberRegex,
                  message: 'Numer piętra musi być liczbą.'
                }
              }}
              noMargin
            />
          </div>
          <div className={styles.row}>
            <ControlledTextField
              id='delivery_address_client_delivery_notes'
              label='Wskazówki dla kuriera'
              placeholder='np. kod do bramy'
              name='clientDeliveryNotes'
              rules={{}}
              noMargin
            />
          </div>
          <div className={styles.row}>
            <Button
              id='delivery_address_save_button'
              disabled={submitState === ResponseStatus.pending}
              type='submit'
              variant='primary'
              size='large'
              fullWidth
            >
              {submitState === ResponseStatus.pending ? 'Wysyłam...' : 'Zapisz'}
            </Button>
            <Button
              id='delivery_address_cancel_button'
              variant='neutralOutline'
              size='large'
              type='button'
              onClick={handleClose}
              fullWidth
            >
              Anuluj
            </Button>
          </div>
        </form>
      </FormProvider>
    </>
  )
}

export default FormAddDeliveryAddress
